import { useState, useEffect, useCallback, useRef } from 'react';
import { financeService } from '../services/finance';
import {
  FinanceOverview,
  FinanceOverviewParams,
  FinanceLedgerEntry,
  FinanceLedgerParams,
  FinancePaymentItem,
  FinancePaymentsParams,
  FinancePayoutItem,
  FinancePayoutsParams,
  FinanceRefundItem,
  FinanceRefundsParams,
  FinanceCancellationItem,
  FinanceCancellationsParams,
  FinanceCancellationsSummary,
  FinanceOperationItem,
  FinanceOperationsParams,
} from '../types/finance';

// ============================================================================
// useFinanceOverview
// ============================================================================

interface UseFinanceOverviewState {
  data: FinanceOverview | null;
  loading: boolean;
  error: string | null;
}

interface UseFinanceOverviewReturn extends UseFinanceOverviewState {
  refetch: () => Promise<void>;
}

export function useFinanceOverview(params: FinanceOverviewParams = {}): UseFinanceOverviewReturn {
  const [state, setState] = useState<UseFinanceOverviewState>({
    data: null,
    loading: true,
    error: null,
  });

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await financeService.getFinanceOverview(paramsRef.current);
      setState({ data: response.data, loading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch finance overview';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch, params.preset, params.startDate, params.endDate]);

  return { ...state, refetch: fetch };
}

// ============================================================================
// Generic paginated state / return types
// ============================================================================

interface PaginatedState<T> {
  items: T[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
}

interface PaginatedReturn<T> extends PaginatedState<T> {
  refetch: () => Promise<void>;
}

// ============================================================================
// useFinanceLedger
// ============================================================================

export function useFinanceLedger(params: FinanceLedgerParams): PaginatedReturn<FinanceLedgerEntry> {
  const limit = params.limit || 20;
  const offset = params.offset || 0;

  const [state, setState] = useState<PaginatedState<FinanceLedgerEntry>>({
    items: [],
    total: 0,
    loading: true,
    error: null,
    page: Math.floor(offset / limit) + 1,
    totalPages: 0,
  });

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await financeService.getFinanceLedger(paramsRef.current);
      const { items, total, limit: rl, offset: ro } = response.data;
      setState({
        items,
        total,
        loading: false,
        error: null,
        page: Math.floor(ro / rl) + 1,
        totalPages: Math.ceil(total / rl),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch ledger';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [
    fetch,
    params.entryType,
    params.status,
    params.account,
    params.search,
    params.startDate,
    params.endDate,
    params.minAmount,
    params.maxAmount,
    params.offset,
    params.limit,
  ]);

  return { ...state, refetch: fetch };
}

// ============================================================================
// useFinancePayments
// ============================================================================

export function useFinancePayments(params: FinancePaymentsParams): PaginatedReturn<FinancePaymentItem> {
  const limit = params.limit || 20;
  const offset = params.offset || 0;

  const [state, setState] = useState<PaginatedState<FinancePaymentItem>>({
    items: [],
    total: 0,
    loading: true,
    error: null,
    page: Math.floor(offset / limit) + 1,
    totalPages: 0,
  });

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await financeService.getFinancePayments(paramsRef.current);
      const { items, total, limit: rl, offset: ro } = response.data;
      setState({
        items,
        total,
        loading: false,
        error: null,
        page: Math.floor(ro / rl) + 1,
        totalPages: Math.ceil(total / rl),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch payments';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [
    fetch,
    params.status,
    params.paymentMethod,
    params.cardBrand,
    params.search,
    params.startDate,
    params.endDate,
    params.offset,
    params.limit,
  ]);

  return { ...state, refetch: fetch };
}

// ============================================================================
// useFinancePayouts
// ============================================================================

export function useFinancePayouts(params: FinancePayoutsParams): PaginatedReturn<FinancePayoutItem> {
  const limit = params.limit || 20;
  const offset = params.offset || 0;

  const [state, setState] = useState<PaginatedState<FinancePayoutItem>>({
    items: [],
    total: 0,
    loading: true,
    error: null,
    page: Math.floor(offset / limit) + 1,
    totalPages: 0,
  });

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await financeService.getFinancePayouts(paramsRef.current);
      const { items, total, limit: rl, offset: ro } = response.data;
      setState({
        items,
        total,
        loading: false,
        error: null,
        page: Math.floor(ro / rl) + 1,
        totalPages: Math.ceil(total / rl),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch payouts';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [
    fetch,
    params.status,
    params.type,
    params.search,
    params.startDate,
    params.endDate,
    params.offset,
    params.limit,
  ]);

  return { ...state, refetch: fetch };
}

// ============================================================================
// useFinanceRefunds
// ============================================================================

export function useFinanceRefunds(params: FinanceRefundsParams): PaginatedReturn<FinanceRefundItem> {
  const limit = params.limit || 20;
  const offset = params.offset || 0;

  const [state, setState] = useState<PaginatedState<FinanceRefundItem>>({
    items: [],
    total: 0,
    loading: true,
    error: null,
    page: Math.floor(offset / limit) + 1,
    totalPages: 0,
  });

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await financeService.getFinanceRefunds(paramsRef.current);
      const { items, total, limit: rl, offset: ro } = response.data;
      setState({
        items,
        total,
        loading: false,
        error: null,
        page: Math.floor(ro / rl) + 1,
        totalPages: Math.ceil(total / rl),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch refunds';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [
    fetch,
    params.startDate,
    params.endDate,
    params.minAmount,
    params.maxAmount,
    params.offset,
    params.limit,
  ]);

  return { ...state, refetch: fetch };
}

// ============================================================================
// useFinanceCancellations
// ============================================================================

interface CancellationsReturn extends PaginatedReturn<FinanceCancellationItem> {
  summary: FinanceCancellationsSummary | null;
}

export function useFinanceCancellations(params: FinanceCancellationsParams): CancellationsReturn {
  const limit = params.limit || 20;
  const offset = params.offset || 0;

  const [state, setState] = useState<PaginatedState<FinanceCancellationItem> & { summary: FinanceCancellationsSummary | null }>({
    items: [],
    total: 0,
    loading: true,
    error: null,
    page: Math.floor(offset / limit) + 1,
    totalPages: 0,
    summary: null,
  });

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await financeService.getFinanceCancellations(paramsRef.current);
      const { items, total, limit: rl, offset: ro, summary } = response.data;
      setState({
        items,
        total,
        loading: false,
        error: null,
        page: Math.floor(ro / rl) + 1,
        totalPages: Math.ceil(total / rl),
        summary,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch cancellations';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [
    fetch,
    params.cancellationType,
    params.status,
    params.startDate,
    params.endDate,
    params.offset,
    params.limit,
  ]);

  return { ...state, refetch: fetch };
}

// ============================================================================
// useFinanceOperations
// ============================================================================

export function useFinanceOperations(params: FinanceOperationsParams): PaginatedReturn<FinanceOperationItem> {
  const limit = params.limit || 20;
  const offset = params.offset || 0;

  const [state, setState] = useState<PaginatedState<FinanceOperationItem>>({
    items: [],
    total: 0,
    loading: true,
    error: null,
    page: Math.floor(offset / limit) + 1,
    totalPages: 0,
  });

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await financeService.getFinanceOperations(paramsRef.current);
      const { items, total, limit: rl, offset: ro } = response.data;
      setState({
        items,
        total,
        loading: false,
        error: null,
        page: Math.floor(ro / rl) + 1,
        totalPages: Math.ceil(total / rl),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch operations';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [
    fetch,
    params.operationType,
    params.status,
    params.search,
    params.startDate,
    params.endDate,
    params.offset,
    params.limit,
  ]);

  return { ...state, refetch: fetch };
}
